<template>
  <div v-if="Object.keys(map).length">
    <div class="map-sidebar-overlay d-flex flex-row">
      <div class="map-sidebar flex-grow-1">
        <div class="px-4 mt-2">
          <div class="text-h6">Place Order</div>
        </div>
        <!--
        height calculation below allows us to keep the title
        and reset/create footer buttons sticky while providing
        a scroll bar if the panels expanded are greater than
        the available height
        64px = app header height
        48px = title height + 8px top margin
        68px = clear/place footer height
      -->
        <div
          style="
            max-height: calc(100vh - (64px + 48px + 68px + 20px));
            overflow: auto;
          "
          class="px-4"
        >
          <v-expansion-panels v-model="panel" multiple accordion tile focusable>
            <v-expansion-panel id="order-params-panel">
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <div>Order Parameters</div>
                </div>
              </v-expansion-panel-header>
              <!-- eager attribute makes sure to render the query-params component so we have a ref to it on mount -->
              <v-expansion-panel-content eager>
                <order-params
                  :select_id="$route.params.id"
                  :map="map"
                  :map_loaded="map_loaded"
                  ref="orderParams"
                  @change="onQueryParamsChange"
                  @mounted="onOrderParamsMounted"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel id="dry-run-panel" v-if="dry_run_results">
              <v-expansion-panel-header
                >Dry Run Results</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <v-container>
                  <span class="pl-4">
                    Estimated Order Total: ${{
                      formatNumber([dry_run_results.usage.cost])[0]
                        .total_imagery_cost
                    }}
                  </span>
                  <v-list-group :value="false">
                    <template v-slot:activator>
                      <v-list-item-title style="font-size: 13px"
                        >Pricing Details</v-list-item-title
                      >
                    </template>
                    <v-list-item>
                      <v-tabs style="border: 1px solid #4c4b48">
                        <v-tab>Estimated Cost</v-tab>
                        <v-tab-item>
                          <v-data-table
                            :headers="cost_headers"
                            :items="formatNumber([dry_run_results.usage.cost])"
                            hide-default-footer
                            dense
                          >
                          </v-data-table>
                        </v-tab-item>
                        <v-tab>Estimated Area</v-tab>
                        <v-tab-item>
                          <v-data-table
                            :headers="area_headers"
                            :items="formatNumber([dry_run_results.usage.area])"
                            hide-default-footer
                            dense
                          >
                          </v-data-table>
                        </v-tab-item>
                      </v-tabs>
                    </v-list-item>
                  </v-list-group>
                </v-container>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <aoi-panel
              ref="aoiPanel"
              :map="map"
              :optional="true"
              @mounted="onAoiMounted"
            />
          </v-expansion-panels>
        </div>
        <div v-if="map" class="pa-4 d-flex justify-space-between">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="clear-button"
                v-if="mounted"
                :disabled="
                  ((Array.isArray($refs.aoiPanel.aois) &&
                    $refs.aoiPanel.aois.length > 0) ||
                  areParamsDirty
                    ? false
                    : true) || pending
                "
                v-bind="attrs"
                v-on="on"
                @click="onClear"
                outlined
                >Clear</v-btn
              >
            </template>
            Remove AOI's and reset order parameters
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="order-button"
                v-if="mounted"
                :disabled="isInvalidForm"
                v-bind="attrs"
                v-on="on"
                @click.prevent="submit"
                color="primary"
                outlined
              >
                <spinner v-if="pending" small> Ordering... </spinner>
                <span v-else> Place Order </span>
              </v-btn>
            </template>
            Place Order based on the AOI's and order parameters chosen
          </v-tooltip>
        </div>
      </div>
    </div>
    <div class="map-search-container px-4">
      <div id="geocoder-container"></div>
    </div>
    <div
      v-show="$refs.aoiPanel && $refs.aoiPanel.processingAreas"
      class="map-loading-overlay"
    >
      <div class="text-center pa-2 white--text">
        <spinner>Loading areas...</spinner>
      </div>
    </div>
    <div v-show="pending" class="map-padded-overlay">
      <div class="text-center pa-2">
        <spinner>Placing Order...</spinner>
      </div>
    </div>
    <confirm-modal
      ref="confirmResetModal"
      title="Clear?"
      confirm-btn-text="Yes, Clear"
      cancel-btn-text="No, Cancel"
      cancel-btn-class="v-btn--flat v-btn--outlined outlined--text"
      confirm-btn-class="black--text ml-4"
    >
      <div style="max-width: 350px">
        Clearing will remove all AOI's and reset the order parameters form. Are
        you sure you want to continue?
      </div>
    </confirm-modal>
  </div>
</template>

<script>
import AoiPanel from "@/components/aoiPanel/AOIPanel";
import OrderParams from "@/components/order/OrderParams";
import Spinner from "@/components/shared/Spinner";
import ConfirmModal from "@/components/shared/ConfirmModal";
import orderApi from "@/api/order";
import mapUtils from "@/utils/map";
import { mapGetters } from "vuex";
import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");
export default {
  props: {
    map: {
      type: Object,
      required: true,
    },
    map_loaded: {
      type: Boolean,
      required: true,
    },
  },
  mixins: [mapUtils],
  components: {
    Spinner,
    ConfirmModal,
    AoiPanel,
    OrderParams,
  },
  data() {
    return {
      panel: [0],
      areParamsValid: true, // by default they are valid
      areParamsDirty: false,
      pending: false,
      aoiPanelMounted: false,
      mounted: false,
      response: null,
      dry_run_results: null,
      cellExtentsSourceName: "Cell Extents",
      area_headers: [
        { text: "Fresh (SqKm)", value: "fresh_imagery_sqkm", sortable: false },
        {
          text: "Standard (SqKm)",
          value: "standard_imagery_sqkm",
          sortable: false,
        },
        {
          text: "Training (SqKm)",
          value: "training_imagery_sqkm",
          sortable: false,
        },
        { text: "Total (SqKm)", value: "total_imagery_sqkm", sortable: false },
      ],
      cost_headers: [
        { text: "Fresh ($)", value: "fresh_imagery_cost", sortable: false },
        {
          text: "Standard ($)",
          value: "standard_imagery_cost",
          sortable: false,
        },
        {
          text: "Training ($)",
          value: "training_imagery_cost",
          sortable: false,
        },
        { text: "Total ($)", value: "total_imagery_cost", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters(["account_details", "user_details"]),
    isInvalidForm() {
      // when placing an order the form is INVALID if we don't have a select_id/acquisition, or are already placing an order
      return (
        (!this.$refs.orderParams.select_id &&
          (!this.$refs.orderParams.model.acquisitions ||
            this.$refs.orderParams.model.acquisitions.length === 0)) ||
        !this.$refs.orderParams.orderParamsValid() ||
        this.pending
      );
    },
    is_internal() {
      if (this.account_details) {
        return this.account_details.internal;
      }
      return false;
    },
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  mounted() {
    this.mounted = true;
  },
  beforeDestroy() {
    this.map.setDrawMode("simple_select");
    this.aoiPanelMounted = false;
    this.mounted = false;
  },
  methods: {
    onAoiMounted() {
      // console.log(`onAoiMounted`);
      this.aoiPanelMounted = true;
      // let the draw controls load before we set the draw mode
      this.$nextTick(() => {
        this.setActivePanel();
        // need to delay setting draw mode to let everything initialize correctly
        setTimeout(
          (() => {
            this.map.setDrawMode("simple_select");
          }).bind(this),
          400
        );
      });
    },
    onOrderParamsMounted() {
      this.$nextTick(() => {
        setTimeout(
          (() => {
            this.initMap();
          }).bind(this),
          400
        );
      });
    },
    initMap() {
      if (this.map_loaded) {
        const cellExtentsLayerId = `${this.cellExtentsSourceName} Outline`;
        this.map.addSourceLayers(
          this.cellExtentsSourceName,
          {
            type: "geojson",
            data: null,
          },
          [
            {
              id: cellExtentsLayerId,
              type: "line",
              source: this.cellExtentsSourceName,
              layout: {},
              paint: {
                "line-color": "rgba(204, 204, 204, 0.8)",
                "line-width": 0.8,
              },
            },
          ]
        );
      } else {
        setTimeout(this.initMap, 200);
      }
    },
    setActivePanel() {
      // console.log(`setActivePanel`);
      this.panel = [0];
    },
    setFeatureCollection(featureCollection) {
      if (Array.isArray(featureCollection.features)) {
        const cleanAois = this.getCleanAois(featureCollection.features);
        this.$refs.aoiPanel.aois = [...cleanAois];
      } else {
        this.$refs.aoiPanel.aois = [];
      }
    },
    onClear() {
      this.$refs.confirmResetModal.show().then((yes) => {
        if (yes) {
          // delete all the aois
          this.map.draw.deleteAll();
          this.$refs.aoiPanel.aois = [];
          this.$refs.orderParams.reset();
        }
      });
    },
    async submit() {
      this.placeOrder();
    },
    async placeOrder() {
      this.pending = true;
      const model = this.$refs.orderParams.model;
      const emailNotification = this.$refs.orderParams.emailNotification;
      const snsNotification = this.$refs.orderParams.snsNotification;
      let output_config = {};
      output_config[this.$refs.orderParams.selectedPlatform] =
        this.$refs.orderParams[this.$refs.orderParams.selectedPlatform];
      const formatted_acquisitions = this.$refs.orderParams.formatAcquisitions(
        model.acquisitions
      );
      if ("azure_blob_storage" in output_config) {
        if (!output_config.azure_blob_storage.credentials_id) {
          delete output_config.azure_blob_storage.credentials_id;
        } else if (!output_config.azure_blob_storage.sas_url) {
          delete output_config.azure_blob_storage.sas_url;
        }
      } else if ("google_cloud_storage" in output_config) {
        if (!output_config.google_cloud_storage.credentials_id) {
          delete output_config.google_cloud_storage.credentials_id;
        } else if (!output_config.google_cloud_storage.service_credentials) {
          delete output_config.google_cloud_storage.service_credentials;
        }
      }
      let settings = model.settings;
      if (!this.is_internal) {
        [
          "visual",
          "ms_analytic",
          "pan_analytic",
          "data_mask",
          "building_mask",
          "p3dr",
        ].forEach((setting) => {
          delete settings[setting];
        });
      }
      var metadata = this.$refs.orderParams.formatOrderMetadata();
      metadata.ui = true;

      let params = {
        dry_run: model.dryRun,
        output_config: output_config,
        acquisitions: formatted_acquisitions,
        metadata: metadata,
        settings: settings,
      };

      let notifications = [];
      if (emailNotification.address) {
        notifications.push(emailNotification)
      }
      if (snsNotification.topic_arn) {
        notifications.push(snsNotification)
      }
      if (notifications.length > 0) {
        params.notifications = notifications;
      }

      if (this.$refs.orderParams.select_id) {
        params.select_id = this.$refs.orderParams.select_id;
        delete params.acquisitions;
      }
      try {
        const intersects = this.getIntersects(
          !this.$refs.orderParams.select_id &&
            this.$refs.orderParams.model.acquisitions.length === 0
        );
        if (intersects) {
          params.intersects = intersects.geometry || intersects;
        }
      } catch (err) {
        this.$log.error(err);
      }
      orderApi
        .place_order(params)
        .then((response) => {
          this.pending = true;
          this.response = response;
          if (this.response && params.dry_run) {
            this.pending = false;
            this.dry_run_results = response.data;
            this.SHOW_SNACKBAR({
              text: "Order dry run was successful.",
              color: "success",
            });
            this.panel = [2];
          } else if (
            this.response &&
            this.response.data &&
            this.response.data.id
          ) {
            // route to the view selection page
            this.$router.push({
              name: "order-details",
              params: {
                account_id: this.user_details.account_id,
                order_id: this.response.data.id,
              },
            });
            this.SHOW_SNACKBAR({
              text: `Order ${this.response.data.id} successfully placed.`,
              color: "success",
            });
          } else {
            let errorMsg = `Unexpected response recieved - ${JSON.stringify(
              this.response
            )}`;
            if (this.response.data && this.response.data.error_message) {
              if (
                this.response.data.error_message.Error &&
                this.response.data.error_message.Cause
              ) {
                errorMsg = `${this.response.data.error_message.Error} - ${this.response.data.error_message.Cause}`;
              } else {
                errorMsg = JSON.stringify(this.response.data.error_message);
              }
            }
            throw new Error(errorMsg);
          }
        })
        .catch(() => {
          this.pending = false;
        });
    },
    onQueryParamsChange(model, { valid, dirty }) {
      /*
      this.$log.debug(
        `onQueryParamsChange(model=${JSON.stringify(
          model
        )}, valid=${valid}, dirty=${dirty})`
      );*/
      this.areParamsValid = valid;
      this.areParamsDirty = dirty;
    },
    formatNumber(usage) {
      Object.entries(usage[0]).forEach(([key, value]) => {
        if (typeof value === "number") {
          usage[0][key] = value.toLocaleString();
        }
      });
      return usage;
    },
  },
};
</script>
